import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowUpRight, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { RevealLines, FadeUp } from "@/components/Reveal";

const fmtDate = (iso) => {
  if (!iso) return "Date TBA";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      weekday: "short", month: "long", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
};
const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
};

const EventCard = ({ e, i, past }) => (
  <motion.article
    data-testid={`event-card-${e.id}`}
    initial={{ opacity: 0, y: 36 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.08 }}
    className={`group border border-ink/15 bg-white ${past ? "opacity-90" : ""}`}
  >
    <div className="hover-img aspect-[16/10] bg-paper relative">
      <img src={e.image} alt={e.title} className="h-full w-full object-cover" />
      <span className="absolute top-4 left-4 label bg-canary text-ink px-3 py-1.5">
        {new Date(e.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
      </span>
    </div>
    <div className="p-6 lg:p-8">
      <div className="flex items-center gap-2 label text-ink/50 mb-3">
        <CalendarDays size={14} /> {fmtDate(e.date)} · {fmtTime(e.date)}
      </div>
      <h3 className="font-display text-2xl lg:text-3xl font-bold leading-tight">{e.title}</h3>
      {e.location && (
        <p className="flex items-center gap-1.5 text-ink/60 mt-3"><MapPin size={15} /> {e.location}</p>
      )}
      <p className="mt-4 text-ink/70 leading-relaxed">{e.description}</p>
      {!past && e.link && (
        <a href={e.link} target="_blank" rel="noopener noreferrer" data-testid={`event-rsvp-${e.id}`}
          className="inline-flex items-center gap-2 mt-6 label bg-ink text-white px-6 py-3 rounded-full hover:bg-canary hover:text-ink transition-colors duration-300">
          RSVP / Details <ArrowUpRight size={15} />
        </a>
      )}
    </div>
  </motion.article>
);

export default function Events() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  const now = Date.now();
  const upcoming = events
    .filter((e) => e.date && new Date(e.date).getTime() >= now)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const past = events
    .filter((e) => !e.date || new Date(e.date).getTime() < now)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div data-testid="events-page">
      <Seo title="Events" description="Upcoming and past events, including our signature Lift Every Voice convenings across Colorado." />
      <section className="pt-[120px] pb-12 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
        <p className="label text-ink/50 mb-6">Events</p>
        <RevealLines as="h1"
          className="font-display font-black text-[13vw] lg:text-[8vw] leading-[0.85] tracking-tight"
          lines={["In the", "room."]}
        />
        <FadeUp delay={0.5}>
          <p className="mt-8 max-w-xl text-lg text-ink/70 leading-relaxed">
            Convenings, listening sessions, and live tapings — where community expertise turns
            into action. Join us in person.
          </p>
        </FadeUp>
      </section>

      {/* Upcoming */}
      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 pb-20">
        <FadeUp><h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight border-t border-ink/15 pt-10 mb-10">Upcoming</h2></FadeUp>
        {upcoming.length ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcoming.map((e, i) => <EventCard key={e.id} e={e} i={i} past={false} />)}
          </div>
        ) : (
          <p data-testid="no-upcoming" className="text-ink/50">No upcoming events right now — check back soon.</p>
        )}
      </section>

      {/* Past archive */}
      <section className="bg-paper py-20">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          <FadeUp><h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mb-10">Past events</h2></FadeUp>
          {past.length ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {past.map((e, i) => <EventCard key={e.id} e={e} i={i} past />)}
            </div>
          ) : (
            <p className="text-ink/50">The archive is being built.</p>
          )}
        </div>
      </section>
    </div>
  );
}
